/**
 * Allotment of Shares — Bonus Issue (M32203) 문서별 변수 정의 + 영문 라벨/타입 규칙.
 *
 * OTC(otcFields.ts)를 미러링. Bonus Issue 는 "한 task = 8문서 패키지 + 공유 values 맵" 이다.
 * (준비금 자본전입으로 기존 주주에게 무상 배정 — 청약자 유형 분기 없음.)
 * 반복부(allottees / share classes)는 flat *_${n} 키로 저장 후 mapper 가 배열로 확장한다.
 */

export type BonusFieldType = 'text' | 'date' | 'entity' | 'person' | 'combo' | 'address' | 'msic';

/** 반복부(allottee / share class) 최대 행 수. */
export const BONUS_ALLOTTEE_MAX = 20;
export const BONUS_SHARE_CLASS_MAX = 10;

// ─── 8개 문서 docCode (실제 docx 템플릿 연결 전까지 placeholder) ───
export type BonusDocCode =
  | 'BON01' // Authority to Issue Shares & Capitalisation of Reserves (DCR + MWR)
  | 'BON02' // Letter of Application for Shares
  | 'BON03' // Letter of Allotment (Bonus Issue)
  | 'BON04' // Increase of Paid-Up Capital (Bonus Issue) (DCR)
  | 'BON05' // Section 76 Notice — Director
  | 'BON06' // Section 76 Notice — Secretary
  | 'BON07' // Statement of Shares Allotted (Bonus Issue)
  | 'BON08'; // Section 78 — Return of Allotment

export interface BonusDocMeta {
  docCode: BonusDocCode;
  label: string;
  stage: number;   // 1~4 (작성 흐름 단계)
  icon: string;    // 스텝 라벨 이모지
  /** 채우기 대상(스칼라) 변수 — progress/검증 기준. 반복/조건/Signers 는 제외. */
  keys: string[];
  /** 문서가 쓰는 반복부 종류. */
  loops?: Array<'allottees' | 'shares'>;
}

// ─── 공통(여러 문서 공유) 스칼라 ──────────────────────────────────
const COMPANY = ['company_name', 'company_registration_no', 'old_registration_no'];
const COSEC = ['cosec_name', 'cosec_address'];
const RESERVE = ['reserve_amount', 'reserve_type', 'share_price'];
const RATIO = ['bonus_ratio_new', 'bonus_ratio_new_words', 'bonus_ratio_existing', 'bonus_ratio_existing_words', 'record_date'];

export const BONUS_DOCS: Record<BonusDocCode, BonusDocMeta> = {
  // ── Stage 1 — Board Approval & Capitalisation of Reserves ──
  // label = 실제 Bonus Issue 템플릿 ORG_FILE_NAME(확장자 제외) 과 동일하게 유지.
  BON01: {
    docCode: 'BON01',
    label: 'Authority to Issue Shares & Capitalisation of Reserves (DCR+MWR)',
    stage: 1, icon: '⚖️',
    keys: [...COMPANY, 'constitution_clause_number', ...RESERVE, ...RATIO, 'dcr_date', 'circulation_date', ...COSEC, 'deadline_date'],
  },

  // ── Stage 2 — Application & Letter of Allotment ──
  BON02: {
    docCode: 'BON02', label: 'Share Application Letter', stage: 2, icon: '🙍',
    keys: [
      'letter_date', 'company_name', 'company_address', 'shares_subscribed', 'share_price',
      'subscriber_name', 'subscriber_id', 'subscriber_address', 'subscriber_type', 'subscriber_dob', 'subscriber_nationality',
      'politician_details', 'pep_connection_details', 'govt_position_details',
      'bo_full_name', 'bo_nature', 'bo_place', 'bo_registration_no', 'bo_registered_address', 'bo_business_address',
      'shares_offered', 'shares_accepted',
    ],
  },
  BON03: {
    docCode: 'BON03', label: 'Letter of Allotment (Bonus Issue)', stage: 2, icon: '✉️',
    keys: [...COMPANY, 'letter_date', ...COSEC, 'capitalized_amount', 'total_new_shares'],
    loops: ['allottees'],
  },

  // ── Stage 3 — Allotment Resolution & Capital Increase ──
  BON04: {
    docCode: 'BON04', label: 'Increase of Paid-Up Capital (Bonus Issue)', stage: 3, icon: '📈',
    keys: [...COMPANY, 'constitution_clause_number', 'mwr_date', ...RESERVE, ...RATIO, 'total_new_shares', 'dcr_date'],
    loops: ['allottees'],
  },
  BON05: {
    docCode: 'BON05', label: 'Section 76 Notice (Director)', stage: 3, icon: '📄',
    keys: [...COMPANY, 'allotment_date', 'conditions_text', 'signature_date'],
  },
  BON06: {
    docCode: 'BON06', label: 'Section 76 Notice (Secretary)', stage: 3, icon: '📄',
    keys: [
      ...COMPANY, 'allotment_date', 'conditions_text',
      'secretary_name', 'secretary_license_no', 'secretary_sspc_no', 'signature_date',
      'lodger_name', 'lodger_nric', 'lodger_address', 'lodger_phone', 'lodger_email',
    ],
  },

  // ── Stage 4 — Statutory Lodgement ──
  BON07: {
    docCode: 'BON07',
    label: 'Statement Containing Particulars of Shares Allotted Otherwise Than for Cash (Bonus Issue)',
    stage: 4, icon: '📊',
    keys: [
      ...COMPANY, 'total_new_shares', 'share_price', 'allottee_names_summary', 'allotment_date',
      'reserve_type', 'statement_date',
    ],
    loops: ['allottees'],
  },
  BON08: {
    docCode: 'BON08', label: 'Section 78 Return of Allotment (Director Sign)', stage: 4, icon: '📋',
    keys: [
      ...COMPANY, 'allotment_date', 'period_start_date', 'period_end_date',
      'total_current_cash', 'total_current_otherwise', 'total_current',
      'total_additional_cash', 'total_accumulated_cash', 'total_additional_otherwise', 'total_accumulated_otherwise',
      'total_additional', 'total_accumulated',
      'director_name', 'director_nric', 'signature_date',
    ],
    loops: ['shares', 'allottees'],
  },
};

export const BONUS_DOC_CODES = Object.keys(BONUS_DOCS) as BonusDocCode[];

/** 4단계 스텝 정의 (스텝퍼 표시용). */
export const BONUS_STAGES: Array<{ no: number; en: string }> = [
  { no: 1, en: 'Board Approval & Capitalisation of Reserves' },
  { no: 2, en: 'Application & Letter of Allotment' },
  { no: 3, en: 'Allotment Resolution & Capital Increase' },
  { no: 4, en: 'Statutory Lodgement' },
];

// ─── 라벨 ────────────────────────────────────────────────────────
const OVERRIDES: Record<string, string> = {
  company_name: 'Company Name', company_registration_no: 'Company Registration No.', old_registration_no: 'Old Registration No.',
  company_address: 'Registered Office Address',
  letter_date: 'Letter Date', dcr_date: 'DCR Date', mwr_date: 'MWR Date', circulation_date: 'Circulation Date', deadline_date: 'Deadline Date',
  allotment_date: 'Allotment Date', signature_date: 'Signature Date', statement_date: 'Statement Date', record_date: 'Record Date',
  period_start_date: 'Period Start', period_end_date: 'Period End',
  constitution_clause_number: 'Clause / Article No.',
  cosec_name: 'Company Secretary Name', cosec_address: 'Company Secretary Address',
  reserve_amount: 'Reserve Amount (RM)', reserve_type: 'Reserve Type', share_price: 'Share Price (RM)',
  bonus_ratio_new: 'New Ratio (No.)', bonus_ratio_new_words: 'New Ratio (words)',
  bonus_ratio_existing: 'Existing Ratio (No.)', bonus_ratio_existing_words: 'Existing Ratio (words)',
  capitalized_amount: 'Capitalized Amount (RM)', total_new_shares: 'Total New Shares', allottee_names_summary: 'Allottee Names (summary)',
  subscriber_name: 'Subscriber Name', subscriber_id: 'ID / Passport / Company No.', subscriber_address: 'Subscriber Address',
  subscriber_type: 'Person / Company', subscriber_dob: 'Date of Birth', subscriber_nationality: 'Nationality',
  shares_subscribed: 'No. of Shares Subscribed', shares_offered: 'No. of Shares Offered', shares_accepted: 'No. of Shares Accepted',
  politician_details: 'Politician Details', pep_connection_details: 'PEP Connection Details', govt_position_details: 'Govt Position Details',
  bo_full_name: 'Beneficial Owner Name', bo_nature: 'Nature of Business (MSIC)', bo_place: 'Place of Registration', bo_registration_no: 'BO Registration No.',
  bo_registered_address: 'BO Registered Address', bo_business_address: 'BO Business Address',
  secretary_name: 'Secretary Name', secretary_license_no: 'License / Membership No.', secretary_sspc_no: 'SSM PC No.',
  lodger_name: 'Lodger Name', lodger_nric: 'Lodger NRIC', lodger_address: 'Lodger Address', lodger_phone: 'Lodger Phone', lodger_email: 'Lodger Email',
  conditions_text: 'Conditions',
  director_name: 'Director Name', director_nric: 'Director NRIC',
  total_current_cash: 'Current — Cash', total_current_otherwise: 'Current — Otherwise', total_current: 'Current — Total',
  total_additional_cash: 'Additional — Cash', total_accumulated_cash: 'Accumulated — Cash',
  total_additional_otherwise: 'Additional — Otherwise', total_accumulated_otherwise: 'Accumulated — Otherwise',
  total_additional: 'Additional — Total', total_accumulated: 'Accumulated — Total',
  // repeating row fields
  allottee_name: 'Allottee Name', allottee_address: 'Allottee Address', allottee_shares: 'No. of Shares',
  allottee_id_no: 'Allottee NRIC / Reg. No.',
  allottee_amount: 'Amount Capitalized (RM)',
  allottee_amount_capitalized: 'Amount Capitalized (RM)',
  allottee_shares_otherwise: 'Shares Allotted Otherwise than in Cash',
  allottee_share_type: 'Class of Shares',
  share_class_type: 'Type', share_class_details: 'Details of Shares', share_class_number: 'Number of Shares',
  share_class_price: 'Price Per Share (RM)', share_class_total: 'Total Issued (RM)',
};

const TOKENS: Record<string, string> = {
  no: 'No.', id: 'ID', nric: 'NRIC', bo: 'BO', dcr: 'DCR', pep: 'PEP', mwr: 'MWR', sspc: 'SSM PC',
};

/** allottee_name_3 / share_class_number_2 등 뒤 인덱스 제거한 base key. */
function stripIndex(key: string): string {
  return key.replace(/_\d+$/, '');
}

export function labelize(key: string): string {
  const base = stripIndex(key);
  if (OVERRIDES[base]) return OVERRIDES[base];
  return base.split('_').map((w) => TOKENS[w] || (w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
}

export function typeOf(key: string): BonusFieldType {
  const b = stripIndex(key);
  if (/(^|_)(date|dob)$/.test(b) || /_date$/.test(b)) return 'date';
  // Place of Registration(bo_place) = Nationality 와 동일 콤보
  if (/nationality$/.test(b) || b === 'bo_place') return 'combo';
  if (/(^|_)race$/.test(b)) return 'combo';
  if (/(^|_)occupation$/.test(b)) return 'combo';
  // Class of Shares / Share Type — Ordinary / Preference / …
  if (b === 'allottee_share_type' || b === 'share_class_type') return 'combo';
  // Nature of Business(bo_nature) = MSIC 코드 챗봇 조회
  if (b === 'bo_nature') return 'msic';
  if (/_address$/.test(b)) return 'address';
  if (/(_name$|allottee_name$|director_name$|secretary_name$|cosec_name$|lodger_name$|bo_full_name$|subscriber_name$)/.test(b)
      && b !== 'company_name' && b !== 'allottee_names_summary') return 'person';
  return 'text';
}

export type BonusComboSource = 'nationality' | 'occupation' | 'country' | 'race' | 'shareClass';
export function comboSourceOf(key: string): BonusComboSource | null {
  const b = stripIndex(key);
  if (/nationality$/.test(b) || b === 'bo_place') return 'nationality';
  if (/(^|_)race$/.test(b)) return 'race';
  if (/(^|_)occupation$/.test(b)) return 'occupation';
  if (b === 'allottee_share_type' || b === 'share_class_type') return 'shareClass';
  return null;
}

/** 날짜/조건상세 등은 선택 입력. progress 계산에서 제외. */
export function isOptional(key: string): boolean {
  const b = stripIndex(key);
  if (/(_date|_dob)$/.test(b)) return true;
  if (/^(old_registration_no|conditions_text|politician_details|pep_connection_details|govt_position_details|period_start_date|period_end_date|allottee_names_summary)$/.test(b)) return true;
  if (/^total_/.test(b)) return true;
  // Bonus Issue 핵심과 거리가 먼 보조 입력(현금 청약/실소유자/lodger 연락처)은 진행률에서 제외 —
  //   무상배정에는 통상 필요치 않아, 이들을 포함하면 진행률이 낮게 캡(예: ~24%)되는 문제 방지.
  if (/^(subscriber_|bo_|lodger_)/.test(b)) return true;
  if (/^(shares_subscribed|shares_offered|shares_accepted)$/.test(b)) return true;
  return false;
}

// ─── Governing Document(정관 결의 근거) clause 파싱 — OTC/AOD 동일 ───
/** "Article 4(1)" → "4(1)" / "Clause 15" → "15" / 그 외 첫 숫자 token. */
export function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association|schedule/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

/** 등록 clause 문자열 → resolution_basis 종류. 우선순위 Third Schedule > Articles > Constitution. */
export function clauseBasisOf(text: string): 'constitution' | 'articles' | 'third_schedule' | 'other' {
  const s = String(text || '');
  if (/third\s*schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s) || /\btable\s+a\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return 'other';
}
